// The SOURCE GRAPH domain model - the canonical, layered description of every
// Jewish source, text, connection and journey step the app can ever show.
//
// WHY LAYERS (do not collapse them):
//   Canonical Torah layer  - Work / TextVersion / Passage / NormalizedRef /
//                            PassageAddress: global, user-independent truth.
//   Connection layer       - SourceEdge / Evidence / TextSpan / Provenance /
//                            ReviewStatus: typed relationships WITH proof.
//   Knowledge layer        - Person / Topic: who wrote what, what it discusses.
//   User journey layer     - JourneyNode / SavedJourney / PinnedSource /
//                            Checkpoint: session-specific, NEVER global truth.
//   Query layer            - SourceGraphQuery / SourceGraphResult: how the UI
//                            asks for connections (grouped, trust-ordered).
//
// CORE RULE (extends the ACCURACY-LOCK in sefaria.ts): a connection may only be
// DISPLAYED if the model can answer - what is it, what passage, what work, what
// version/provider, what does it connect to, what relationship, what EVIDENCE,
// where the evidence came from, how confident we are, and whether it was
// reviewed. AI may summarize/label/suggest - it is NEVER the source of truth
// for a citation. Citation truth comes from structured provider data,
// deterministic parsing (citations.ts dual gate), exact quote matching, or
// manual/editorial review.
//
// This module is types-only (plus tiny type guards): no DOM, no network, no
// imports from views. Everything downstream (adapters, validation, trust
// badges, query, fixtures, UI) builds on these shapes.

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export type SourceCategory =
  | "Tanach"
  | "Mishnah"
  | "Talmud"
  | "Midrash"
  | "Commentary"
  | "Halacha"
  | "Responsa"
  | "Chassidut"
  | "Mussar"
  | "Kabbalah"
  | "Philosophy"
  | "Liturgy"
  | "Other";

export type SourceProvider = "Sefaria" | "Wikisource" | "Manual" | "Internal" | "Other";

// How confident we are that a RELATIONSHIP holds. "verified" is reserved for
// structural truth, exact quote matches, and human review - never AI output.
export type Confidence = "verified" | "high" | "medium" | "low" | "suggested" | "rejected";

export type Visibility = "public" | "hidden" | "admin_only" | "experimental";

export type LanguageCode = "he" | "en" | "ar" | "yi" | "ladino" | "other";

export type DateRange = {
  start?: number; // year (negative = BCE)
  end?: number;
  approximate?: boolean;
  label?: string; // e.g. "11th century"
};

export type LicenseInfo = {
  name?: string;
  url?: string;
  attributionRequired?: boolean;
  notes?: string;
};

// An identifier in a provider's namespace (e.g. a Sefaria index title).
export type ProviderId = {
  provider: SourceProvider;
  id: string;
};

// A provider's own way of writing a ref, with optional locator URL / version.
export type ProviderRef = {
  provider: SourceProvider;
  ref: string;
  url?: string;
  versionId?: string;
};

// ---------------------------------------------------------------------------
// Knowledge layer: Person / Topic (defined first - the canonical layer uses
// PersonRef / TopicRef)
// ---------------------------------------------------------------------------

export type Person = {
  id: string;

  primaryName: string; // e.g. "Rashi"
  heName?: string;

  alternateNames?: string[]; // "Shlomo Yitzchaki", "R. Solomon ben Isaac"
  abbreviations?: string[];

  birthYearApprox?: number;
  deathYearApprox?: number;

  era?:
    | "Tanach"
    | "Tannaim"
    | "Amoraim"
    | "Geonim"
    | "Rishonim"
    | "Acharonim"
    | "Modern"
    | "Contemporary"
    | "Unknown";

  region?: string;

  associatedWorks?: string[]; // Work ids

  metadata?: Record<string, unknown>;
};

export type PersonRef = {
  personId: string;
  role:
    | "author"
    | "commentator"
    | "translator"
    | "editor"
    | "compiler"
    | "attributed_to"
    | "mentioned";
};

// A topic/theme. Topic matches are EXPLORATORY - a topic connection is never
// the same kind of claim as a verified citation, and the UI must keep them
// visually quieter (see sourceTrust.ts).
export type Topic = {
  id: string;

  name: string;
  heName?: string;

  aliases?: string[];

  parentTopicId?: string;

  category?:
    | "halacha"
    | "parsha"
    | "hashkafa"
    | "middot"
    | "tanach_theme"
    | "talmudic_concept"
    | "language"
    | "history"
    | "other";

  description?: string;

  source: "manual" | "provider" | "algorithm" | "ai_suggested";

  confidence?: "verified" | "probable" | "suggested";
};

export type TopicRef = {
  topicId: string;
  relevance: 1 | 2 | 3 | 4 | 5;
  evidenceIds?: string[];
};

// ---------------------------------------------------------------------------
// Canonical Torah layer: Work / TextVersion / Passage / NormalizedRef /
// PassageAddress
// ---------------------------------------------------------------------------

// The book/work/collection itself - NOT a specific passage. "Numbers",
// "Rashi on Numbers", "Chagigah", "Mishnah Berurah" are Works; "Numbers 13:16"
// is a Passage WITHIN a Work.
export type Work = {
  id: string;

  canonicalTitle: string; // e.g. "Rashi on Numbers"
  heTitle?: string;

  alternateTitles?: string[];
  abbreviations?: string[];

  category: SourceCategory;
  subcategory?: string;

  authors?: PersonRef[];

  compositionDate?: DateRange;
  publicationDate?: DateRange;

  languagePrimary?: LanguageCode;

  // The work this one is structurally ON: "Rashi on Numbers" -> "numbers";
  // "Tosafot on Chagigah" -> "chagigah". Undefined for independent works.
  baseWorkId?: string;

  commentaryType?:
    | "commentary_on_tanach"
    | "commentary_on_talmud"
    | "commentary_on_halacha"
    | "supercommentary"
    | "anthology"
    | "independent_work";

  sourceProviderIds?: ProviderId[];

  license?: LicenseInfo;

  status?: "active" | "draft" | "deprecated" | "needs_review";

  metadata?: Record<string, unknown>;
};

// A specific text version/edition/translation of a Work - because "Rashi" the
// work and "the Vilna-edition Hebrew of Rashi that Sefaria serves" are
// different claims. The reader's version provenance line (sefaria.ts
// heVersionTitle/versionTitle) is exactly this layer.
export type TextVersion = {
  id: string;

  workId: string;

  title: string; // e.g. "Tanach with Ta'amei Hamikra"
  heTitle?: string;

  language: LanguageCode;

  versionType:
    | "original"
    | "translation"
    | "edition"
    | "transliteration"
    | "summary"
    | "commentary_translation";

  provider: SourceProvider;

  providerVersionId?: string;
  sourceUrl?: string;

  translator?: PersonRef[];
  editor?: PersonRef[];

  license?: LicenseInfo;

  reliability: "authoritative" | "standard" | "community" | "machine_generated" | "unknown";

  hasHebrew?: boolean;
  hasEnglish?: boolean;

  segmentCount?: number;

  createdAt?: string; // ISO timestamps throughout
  updatedAt?: string;
};

// How a passage is ADDRESSED inside its work. Jewish texts use different
// systems - never force everything into chapter:verse.
export type PassageAddress =
  | {
      system: "chapter_verse"; // Tanach
      chapter: number;
      verse?: number;
    }
  | {
      system: "daf_amud"; // Gemara
      daf: number;
      amud: "a" | "b";
    }
  | {
      system: "siman_seif"; // Shulchan Aruch / Mishnah Berurah
      siman: number | string;
      seif?: number | string;
    }
  | {
      system: "chapter_mishnah"; // Mishnah
      chapter: number;
      mishnah: number;
    }
  | {
      system: "paragraph"; // commentaries, free-form sections
      sectionPath: Array<number | string>;
    }
  | {
      system: "custom";
      path: Array<number | string>;
      label?: string;
    };

// The result of normalizing a ref string ("Bamidbar 13:16", "Num. 13:16",
// "Chagiga 12a") to ONE stable canonical ref. See normalizeRef.ts.
export type NormalizedRef = {
  originalInput?: string;

  canonicalRef: string; // "Numbers 13:16"
  heRef?: string;

  workId: string;
  passageId?: string;

  // "Numbers 13:16" => [13, 16]; "Chagigah 12a" => [12, "a"]
  sections: Array<string | number>;

  // For ranges ("Numbers 13:16-18" => toSections [13, 18]).
  toSections?: Array<string | number>;

  isRange: boolean;

  normalizedBy: "provider" | "manual_map" | "parser" | "admin_review";

  confidence: "verified" | "probable" | "failed";

  warnings?: string[];
};

// What normalizeRef() returns: success carries the normalized ref; failure
// carries an explicit error. Bad refs are NEVER silently accepted.
export type NormalizedRefResult = {
  input: string;
  valid: boolean;
  canonicalRef?: string;
  heRef?: string;
  workId?: string;
  passageId?: string;
  category?: SourceCategory;
  normalizedRef?: NormalizedRef;
  warnings?: string[];
  error?: string;
};

// The addressable unit users open, read, cite, save and connect. Every journey
// node points to a Passage; every edge connects Passage to Passage.
export type Passage = {
  id: string;

  workId: string;
  versionIds?: string[]; // TextVersions this passage's text came from

  canonicalRef: string; // "Rashi on Numbers 13:16"
  heRef?: string;

  normalizedRef: NormalizedRef;

  refType:
    | "verse"
    | "chapter"
    | "daf"
    | "amud"
    | "mishnah"
    | "siman"
    | "seif"
    | "paragraph"
    | "section"
    | "range"
    | "custom";

  address: PassageAddress;

  displayTitle: string;
  heDisplayTitle?: string;

  text?: {
    he?: string;
    en?: string;
  };

  textAvailable: boolean;

  // The passage this one is structurally ON:
  // "Rashi on Numbers 13:16" -> basePassageId of "Numbers 13:16".
  basePassageId?: string;

  parentPassageId?: string; // containment ("Numbers 13" contains "Numbers 13:16")
  childPassageIds?: string[];

  range?: {
    startRef: string;
    endRef: string;
  };

  topics?: TopicRef[];

  peopleMentioned?: PersonRef[];

  status: "verified" | "valid_ref_missing_text" | "needs_review" | "invalid" | "deprecated";

  providerRefs?: ProviderRef[];

  createdAt?: string;
  updatedAt?: string;
};

// ---------------------------------------------------------------------------
// Connection layer: SourceEdge / Evidence / TextSpan / Provenance /
// ReviewStatus
// ---------------------------------------------------------------------------

export type RelationshipType =
  | "comments_on"
  | "supercomments_on"
  | "cites"
  | "quotes"
  | "alludes_to"
  | "is_quoted_by"
  | "cross_reference"
  | "parallel_source"
  | "same_base_text"
  | "same_phrase"
  | "same_topic"
  | "halachic_source_for"
  | "halachic_ruling_based_on"
  | "disagrees_with"
  | "supports"
  | "explains"
  | "asks_on"
  | "answers"
  | "compares"
  | "contrasts"
  | "translation_of"
  | "variant_of"
  | "manuscript_variant"
  | "editorial_link"
  | "user_created_link"
  | "ai_suggested";

export type EdgeClass =
  | "structural" // provider-structural truth ("Rashi on X" IS on X)
  | "citation"
  | "quotation"
  | "commentary"
  | "halachic_chain"
  | "topic"
  | "linguistic"
  | "editorial"
  | "user"
  | "ai";

// A TYPED relationship between two passages - never a bare URL or loose link.
// The UI derives grouping, ranking, badges and default visibility from
// relationshipType + edgeClass + strength + confidence. AI-suggested and
// thematic edges must NEVER render identically to verified citation edges.
export type SourceEdge = {
  id: string;

  fromPassageId: string;
  toPassageId: string;

  fromRef: string; // canonical refs duplicated for inspectability
  toRef: string;

  // Read as: FROM <relationshipType> TO. "Rashi on Numbers 13:16"
  // comments_on "Numbers 13:16" => from = the Rashi passage.
  relationshipType: RelationshipType;

  direction: "one_way" | "reverse_available" | "bidirectional";

  edgeClass: EdgeClass;

  // 5 = direct verified quote/commentary ... 1 = loose thematic suggestion.
  strength: 1 | 2 | 3 | 4 | 5;

  confidence: Confidence;

  evidenceIds: string[];

  provenance: Provenance;

  reviewed?: ReviewStatus;

  visibility: Visibility;

  createdAt?: string;
  updatedAt?: string;
};

// The PROOF behind an edge. Every serious connection carries at least one
// Evidence record; an edge with no evidence renders as a warning, never as a
// normal verified link (see sourceValidation.ts / sourceTrust.ts).
export type Evidence = {
  id: string;

  edgeId: string;

  evidenceType:
    | "structural_commentary_link" // the ref-shape itself proves it ("X on Y")
    | "explicit_ref" // the text names the cited location (citations.ts dual gate)
    | "quoted_text" // exact quoted phrase match
    | "provider_link" // the provider's own link record
    | "manual_editorial_link"
    | "topic_match"
    | "phrase_match"
    | "user_annotation"
    | "ai_explanation"; // NEVER proof - explanation/labeling only

  sourcePassageId?: string;

  quoteText?: string;
  heQuoteText?: string;

  sourceTextSpan?: TextSpan;
  targetTextSpan?: TextSpan;

  parserName?: string; // for deterministic-parser evidence (e.g. "citedInText")
  parserVersion?: string;

  provider?: SourceProvider | "AI";

  providerLinkId?: string;
  sourceUrl?: string;

  explanation?: string; // human-readable "why" (shown in the Why? modal)

  confidenceScore?: number; // 0..1

  createdAt?: string;
};

// An exact span inside a TextVersion's text - for highlighting the quoted
// phrase / citation that an Evidence record points at.
export type TextSpan = {
  versionId?: string;

  startChar?: number;
  endChar?: number;

  startWord?: number;
  endWord?: number;

  startToken?: number;
  endToken?: number;

  selectedText?: string;
};

// WHERE an edge/passage came from. Every edge must be able to answer: was it
// imported, manual, algorithmic, AI-suggested - and by whom/what version.
export type Provenance = {
  origin:
    | "provider_import"
    | "manual_entry"
    | "admin_created"
    | "algorithm_detected"
    | "ai_suggested"
    | "user_created";

  provider?: SourceProvider;

  providerId?: string;
  providerUrl?: string;

  importedAt?: string;

  importBatchId?: string;

  createdBy?: string;

  algorithm?: {
    name: string;
    version: string;
    parameters?: Record<string, unknown>;
  };

  notes?: string;
};

// Editorial review state. Required reading for AI-suggested links, thematic
// links, fuzzy matches, user links, and halachic chains before they are
// trusted publicly.
export type ReviewStatus = {
  status: "unreviewed" | "reviewed" | "approved" | "rejected" | "needs_correction";

  reviewedBy?: string;
  reviewedAt?: string;

  reviewerRole?: "admin" | "editor" | "rabbinic_reviewer" | "trusted_user";

  notes?: string;
};

// ---------------------------------------------------------------------------
// User journey layer - session/user-specific. A JourneyNode is NOT a Passage:
// the same Passage can appear in many journeys (or twice in one journey via
// different paths). NEVER mix this layer into canonical source truth.
//
// NOTE ON CURRENT WIRING: the live app's single source of truth for the
// linear trail is the open DOM path (navigator.ts, DOM-path-as-truth), and
// the journey chrome is a read-only projection of it. These types model that
// projection (see journeyNodesFromTrail in sourceGraphAdapter.ts); they do
// not replace the DOM contract.
// ---------------------------------------------------------------------------

export type JourneyNode = {
  id: string;

  passageId: string;
  canonicalRef: string;

  parentJourneyNodeId?: string;

  openedViaEdgeId?: string; // the SourceEdge the user followed to get here

  journeyRole: "start" | "main_path" | "side_path" | "checkpoint" | "pinned" | "returned_to";

  openedAt: string;

  userNote?: string;

  depth: number; // 0 = the anchor

  isCurrent?: boolean;
};

export type SavedJourney = {
  id: string;

  title?: string;

  startJourneyNodeId: string;
  currentJourneyNodeId?: string;

  nodes: JourneyNode[];

  createdAt: string;
  updatedAt?: string;

  summary?: string;

  tags?: string[];

  visibility?: "private" | "shared" | "public";
};

export type PinnedSource = {
  id: string;

  journeyId: string;
  passageId: string;
  journeyNodeId?: string;

  pinnedAt: string;

  note?: string;
};

export type Checkpoint = {
  id: string;

  journeyId: string;
  journeyNodeId: string;

  label?: string;
  note?: string;

  createdAt: string;
};

export type UserNote = {
  id: string;

  passageId: string;
  journeyNodeId?: string;

  text: string;

  createdAt: string;
  updatedAt?: string;
};

// ---------------------------------------------------------------------------
// Query layer - how a graph consumer asks for connections, grouped and
// trust-ordered, never as a raw array of links.
//
// WIRING STATUS (honest): querySourceGraph/SourceGraphResult are currently
// SCAFFOLDING exercised by tests, fixtures and the DEV integrity report. The
// live Explore Next panel adapts the navigator's citeData buckets directly
// through sourceGraphAdapter.edgeFromBucket and shares ONLY the group titles
// (GROUP_META, exported from sourceGraphQuery.ts) so the two paths cannot
// drift on wording. Migrating the panel onto querySourceGraph end-to-end is
// the intended future step once a session-level store is the norm.
// ---------------------------------------------------------------------------

export type SourceGraphQuery = {
  currentPassageId: string;

  includeRelationshipTypes?: RelationshipType[];

  includeEdgeClasses?: EdgeClass[];

  minStrength?: 1 | 2 | 3 | 4 | 5;

  confidence?: Confidence[];

  categories?: SourceCategory[];

  lens?:
    | "commentary"
    | "citations"
    | "quoted_by"
    | "halacha"
    | "midrash"
    | "language"
    | "theme"
    | "all";

  includeSuggested?: boolean; // default false: suggested/AI stays behind a reveal

  limit?: number;
};

// @scaffold - not yet wired to the live UI; the navigator's Explore panel adapts
// buckets via sourceGraphAdapter.edgeFromBucket instead. See WIRING STATUS above.
export type SourceGraphResult = {
  current: Passage;

  groups: Array<{
    id: string;
    title: string;
    description?: string;
    edges: SourceEdge[];
  }>;

  // What the query HID and why - so the UI can say "N suggested connections
  // hidden" instead of silently truncating.
  hiddenCounts?: {
    suggested?: number;
    lowConfidence?: number;
    unsupported?: number; // edges with no resolvable evidence / failed validation
    truncated?: number; // dropped by the query's limit (overflow, not distrust)
  };
};

// ---------------------------------------------------------------------------
// Tiny shared guards (used by validation + adapters)
// ---------------------------------------------------------------------------

export const SOURCE_CATEGORIES: readonly SourceCategory[] = [
  "Tanach", "Mishnah", "Talmud", "Midrash", "Commentary", "Halacha", "Responsa",
  "Chassidut", "Mussar", "Kabbalah", "Philosophy", "Liturgy", "Other",
];

export const CONFIDENCE_LEVELS: readonly Confidence[] = [
  "verified", "high", "medium", "low", "suggested", "rejected",
];

export function isSourceCategory(v: unknown): v is SourceCategory {
  return typeof v === "string" && (SOURCE_CATEGORIES as readonly string[]).includes(v);
}

export function isConfidence(v: unknown): v is Confidence {
  return typeof v === "string" && (CONFIDENCE_LEVELS as readonly string[]).includes(v);
}
